import React from 'react';
import { IdealWeekTemplate, PlanGenerationResult, RaceType } from '../../types/athlete';
import { Calendar, Clock, Layers, RefreshCw, ArrowRight, Zap, Droplets, Bike, Footprints } from 'lucide-react';

interface StepPreviewProps {
  result: PlanGenerationResult;
  raceDetails: {
    raceType: RaceType | null;
    raceDate: string | null;
    name: string;
    description: string;
  };
  idealWeekTemplate: IdealWeekTemplate;
  onRegenerate: () => void;
}

const formatDuration = (minutes: number): string => {
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
};

const getDisciplineIcon = (discipline: string) => {
  switch (discipline) {
    case 'swim': return <Droplets className="w-4 h-4 text-blue-400" />;
    case 'bike': return <Bike className="w-4 h-4 text-green-400" />;
    case 'run': return <Footprints className="w-4 h-4 text-orange-400" />;
    case 'strength': return <Zap className="w-4 h-4 text-purple-400" />;
    default: return <Zap className="w-4 h-4 text-gray-400" />;
  }
};

const getDisciplineColor = (discipline: string): string => {
  switch (discipline) {
    case 'swim': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
    case 'bike': return 'bg-green-500/20 text-green-400 border-green-500/30';
    case 'run': return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
    case 'strength': return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
    default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
  }
};

const StepPreview: React.FC<StepPreviewProps> = ({
  result,
  raceDetails,
  idealWeekTemplate,
  onRegenerate,
}) => {
  const { summary, sessions, warnings } = result;

  const cycleWeeks = [
    { label: 'Week 1', scale: '1.00x', focus: 'Baseline' },
    { label: 'Week 2', scale: '1.08x', focus: 'Build' },
    { label: 'Week 3', scale: '1.16x', focus: 'Build' },
    { label: 'Week 4', scale: '0.60x', focus: 'Recovery' },
  ];

  const dayLabels: Record<number, string> = {
    1: 'Mon',
    2: 'Tue',
    3: 'Wed',
    4: 'Thu',
    5: 'Fri',
    6: 'Sat',
    7: 'Sun',
  };

  const idealWeekDays = Object.keys(idealWeekTemplate.days).map(Number);
  const hasIdealWeek = idealWeekDays.some(day => idealWeekTemplate.days[day]?.length);

  const templateNameById = sessions.reduce((acc, session) => {
    if (session.session_template_id) {
      acc[session.session_template_id] = session.session_name;
    }
    return acc;
  }, {} as Record<string, string>);

  const disciplineCounts = sessions.reduce((acc, session) => {
    acc[session.discipline] = (acc[session.discipline] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const sampleWeek = sessions
    .filter(s => s.week_number === Math.ceil(summary.totalWeeks / 2))
    .sort((a, b) => a.day_of_week - b.day_of_week);

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold mb-2">Your Training Plan</h2>
          <p className="text-muted">Review your personalized plan before saving.</p>
        </div>
        <button
          onClick={onRegenerate}
          className="flex items-center space-x-2 px-4 py-2 bg-[#141921] text-[#e2e8f0] rounded-lg hover:bg-[#2d3748] transition-colors"
        >
          <RefreshCw size={16} />
          <span>Regenerate</span>
        </button>
      </div>

      {warnings.length > 0 && (
        <div className="card-base p-4 bg-[rgba(251,191,36,0.1)] border border-[rgba(251,191,36,0.2)]">
          <p className="font-medium text-yellow-400 mb-2">Plan Notes:</p>
          <ul className="space-y-1">
            {warnings.slice(0, 3).map((warning, idx) => (
              <li key={idx} className="text-sm text-[#94a3b8] flex items-start space-x-2">
                <span className="text-yellow-400">•</span>
                <span>{warning}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card-base p-6">
          <div className="flex items-center space-x-3 mb-2">
            <Calendar className="w-5 h-5 text-[#a855f7]" />
            <span className="text-sm text-muted">Duration</span>
          </div>
          <p className="text-3xl font-bold">{summary.totalWeeks} weeks</p>
          <p className="text-sm text-muted mt-1">
            {summary.weeksToRace} weeks until race day
          </p>
        </div>

        <div className="card-base p-6">
          <div className="flex items-center space-x-3 mb-2">
            <Layers className="w-5 h-5 text-[#a855f7]" />
            <span className="text-sm text-muted">Sessions</span>
          </div>
          <p className="text-3xl font-bold">{sessions.length}</p>
          <p className="text-sm text-muted mt-1">
            ~{summary.sessionsPerWeek} per week
          </p>
        </div>

        <div className="card-base p-6">
          <div className="flex items-center space-x-3 mb-2">
            <Clock className="w-5 h-5 text-[#a855f7]" />
            <span className="text-sm text-muted">Weekly Time</span>
          </div>
          <p className="text-3xl font-bold">{formatDuration(summary.weeklyTimeCommitment.average)}</p>
          <p className="text-sm text-muted mt-1">
            Peak: {formatDuration(summary.weeklyTimeCommitment.peak)}
          </p>
        </div>
      </div>

      <div>
        <h3 className="font-semibold text-lg mb-4">Phase Breakdown</h3>
        <div className="flex flex-wrap gap-3">
          {summary.phaseBreakdown.map((phase) => (
            <div
              key={phase.phase}
              className="px-4 py-2 bg-[#141921] rounded-lg border border-[#2d3748]"
            >
              <span className="font-medium">{phase.phase}</span>
              <span className="text-muted ml-2">({phase.weeks} weeks)</span>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h3 className="font-semibold text-lg mb-4">Session Breakdown</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Object.entries(disciplineCounts).map(([discipline, count]) => (
            <div key={discipline} className={`p-4 rounded-lg border ${getDisciplineColor(discipline)}`}>
              <div className="flex items-center space-x-2 mb-2">
                {getDisciplineIcon(discipline)}
                <span className="font-medium capitalize">{discipline}</span>
              </div>
              <p className="text-2xl font-bold">{count} sessions</p>
            </div>
          ))}
        </div>
      </div>

      {hasIdealWeek && (
        <div>
          <h3 className="font-semibold text-lg mb-4">Ideal Week Snapshot</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {Object.entries(idealWeekTemplate.days)
              .map(([dayKey, daySessions]) => ({ day: Number(dayKey), sessions: daySessions }))
              .map(({ day, sessions: daySessions }) => (
                <div key={day} className="bg-[#141921] rounded-lg p-4 space-y-2">
                  <p className="text-sm font-semibold">{dayLabels[day] || `Day ${day}`}</p>
                  {daySessions.length === 0 ? (
                    <p className="text-xs text-muted">Rest day</p>
                  ) : (
                    daySessions
                      .slice()
                      .sort((a, b) => a.orderTime.localeCompare(b.orderTime))
                      .map(session => (
                        <div key={session.id} className="flex items-center justify-between text-xs">
                          <span className="text-muted">{session.orderTime}</span>
                          <span>{templateNameById[session.templateId] || 'Session'}</span>
                        </div>
                      ))
                  )}
                </div>
              ))}
          </div>
        </div>
      )}

      <div>
        <h3 className="font-semibold text-lg mb-4">Build / Recovery Cycle</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {cycleWeeks.map((cycle) => (
            <div key={cycle.label} className="bg-[#141921] rounded-lg p-4">
              <p className="text-xs text-muted">{cycle.label}</p>
              <p className="text-lg font-semibold">{cycle.scale}</p>
              <p className="text-xs text-muted">{cycle.focus}</p>
            </div>
          ))}
        </div>
      </div>

      {sampleWeek.length > 0 && (
        <div>
          <h3 className="font-semibold text-lg mb-4">Sample Week (Week {sampleWeek[0]?.week_number})</h3>
          <div className="space-y-3">
            {sampleWeek.map((session) => (
              <div
                key={session.id}
                className="flex items-center justify-between p-4 bg-[#141921] rounded-lg"
              >
                <div className="flex items-center space-x-4">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${getDisciplineColor(session.discipline)}`}>
                    {getDisciplineIcon(session.discipline)}
                  </div>
                  <div>
                    <p className="font-medium">{session.session_name}</p>
                    <p className="text-sm text-muted">{session.targets?.description || session.discipline}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-medium">{formatDuration(session.planned_duration_minutes)}</p>
                  {session.has_run_walk && (
                    <p className="text-xs text-muted">Run/Walk</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="card-base p-4 bg-[rgba(168,85,247,0.1)] border border-[rgba(168,85,247,0.2)]">
        <div className="flex items-start space-x-3">
          <ArrowRight className="w-5 h-5 text-[#a855f7] mt-0.5" />
          <div>
            <p className="font-medium text-[#e2e8f0]">Ready to start training?</p>
            <p className="text-sm text-[#94a3b8] mt-1">
              Click "Save Plan" to create this training plan. You can customize sessions, move them around,
              and track your progress as you train.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StepPreview;
